import { z } from 'zod';

/** Earliest birth date the athlete table accepts (mirrors its check constraint). */
export const earliestDateOfBirth = '1900-01-01';

/** Names keep their Unicode letters; they are composed (NFC), trimmed and single-spaced. */
export function normalizeName(name: string): string {
  return name.normalize('NFC').trim().replaceAll(/\s+/g, ' ');
}

const nameSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(200));

/** True for a real calendar day written `YYYY-MM-DD`; `2026-02-30` is not one. */
export function isCalendarDate(candidate: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(candidate);
  if (match?.[1] === undefined || match[2] === undefined || match[3] === undefined) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/** A real calendar day no earlier than the oldest birth date the club records. */
export const dateOfBirthSchema = z
  .string()
  .refine(isCalendarDate)
  .refine((candidate) => candidate >= earliestDateOfBirth);

const optionalContactSchema = (maximumLength: number) =>
  z
    .string()
    .trim()
    .max(maximumLength)
    .transform((value) => (value === '' ? null : value))
    .nullable();

const emailSchema = optionalContactSchema(320).refine(
  (value) => value === null || (value.length >= 3 && value.includes('@')),
);
const phoneSchema = optionalContactSchema(40).refine(
  (value) => value === null || value.length >= 3,
);

export const createAthleteRequestSchema = z.strictObject({
  givenName: nameSchema,
  familyName: nameSchema,
  dateOfBirth: dateOfBirthSchema,
  email: emailSchema.default(null),
  phone: phoneSchema.default(null),
  preferredLocale: z.enum(['en', 'hu', 'ro']).nullable().default(null),
  /**
   * An existing athlete with the same name and birth date only produces a warning; sending `true`
   * confirms that this is a different person. Records are never merged automatically.
   */
  confirmDuplicate: z.boolean().default(false),
});
export type CreateAthleteRequest = z.infer<typeof createAthleteRequestSchema>;

/** Only the fields present change; the athlete's `If-Match` version must accompany the request. */
export const updateAthleteRequestSchema = z
  .strictObject({
    givenName: nameSchema.optional(),
    familyName: nameSchema.optional(),
    dateOfBirth: dateOfBirthSchema.optional(),
    email: emailSchema.optional(),
    phone: phoneSchema.optional(),
    preferredLocale: z.enum(['en', 'hu', 'ro']).nullable().optional(),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((request) =>
    ['givenName', 'familyName', 'dateOfBirth', 'email', 'phone', 'preferredLocale'].some(
      (field) => field in request,
    ),
  );
export type UpdateAthleteRequest = z.infer<typeof updateAthleteRequestSchema>;

export const athleteStatuses = ['active', 'archived', 'withdrawn'] as const;
export type AthleteStatus = (typeof athleteStatuses)[number];

/**
 * Archiving or withdrawing needs a reason; reactivation does not. Leaving ends what is still open
 * (club membership, team and group placements, and on withdrawal the guardians' access) as history.
 */
export const changeAthleteStatusRequestSchema = z
  .strictObject({
    status: z.enum(athleteStatuses),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .refine((request) => request.status === 'active' || request.reason !== undefined, {
    path: ['reason'],
  });
export type ChangeAthleteStatusRequest = z.infer<typeof changeAthleteStatusRequestSchema>;

/** What a status change ended, so the caller can tell the user. */
export interface AthleteStatusChangeView {
  athlete: AthleteView;
  clubMembershipsEnded: number;
  teamPlacementsEnded: number;
  groupPlacementsEnded: number;
  guardianLinksEnded: number;
}

/**
 * An athlete as the caller may see them. Contact details are present only for people who manage
 * athlete records; they are omitted rather than blanked, so a client cannot show what it never got.
 */
export interface AthleteView {
  id: string;
  givenName: string;
  familyName: string;
  /** Calendar day, `YYYY-MM-DD`; it carries no time zone. */
  dateOfBirth: string;
  status: AthleteStatus;
  /** Row version; send it in `If-Match` when changing the athlete. */
  version: number;
  email?: string | null;
  phone?: string | null;
  preferredLocale?: string | null;
}

export interface DashboardAthleteView {
  athleteId: string;
  givenName: string;
  familyName: string;
}

export interface DashboardGroupView {
  count: number;
  /** The first few athletes alphabetically; open the Athletes page for the full list. */
  athletes: DashboardAthleteView[];
}

/**
 * What a secretary should look at today. Expiring registrations and evidence arrive with the
 * competition work package and are not part of this view yet.
 */
export interface SecretaryDashboardView {
  /** The club's current day, `YYYY-MM-DD`. */
  today: string;
  missingGuardianLinks: DashboardGroupView;
  pendingGuardianLinks: number;
  pendingImports: number;
  newJoiners: DashboardGroupView;
  newJoinerDays: number;
  inactiveAthletes: DashboardGroupView;
}

/**
 * A guardian's proposal to change contact details or to correct a name or birth date. Nothing on
 * the athlete changes until a secretary approves it.
 */
export const createChangeRequestSchema = z
  .strictObject({
    givenName: nameSchema.optional(),
    familyName: nameSchema.optional(),
    dateOfBirth: dateOfBirthSchema.optional(),
    email: emailSchema.optional(),
    phone: phoneSchema.optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine((request) =>
    ['givenName', 'familyName', 'dateOfBirth', 'email', 'phone'].some((field) => field in request),
  );
export type CreateChangeRequest = z.infer<typeof createChangeRequestSchema>;

export const decideChangeRequestSchema = z
  .strictObject({
    decision: z.enum(['approve', 'reject']),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .refine((request) => request.decision === 'approve' || request.reason !== undefined, {
    path: ['reason'],
  });
export type DecideChangeRequest = z.infer<typeof decideChangeRequestSchema>;

export const changeRequestStatuses = ['pending', 'approved', 'rejected', 'withdrawn'] as const;
export type ChangeRequestStatus = (typeof changeRequestStatuses)[number];

export interface ChangeRequestFields {
  givenName?: string;
  familyName?: string;
  dateOfBirth?: string;
  email?: string | null;
  phone?: string | null;
}

export interface ChangeRequestView {
  id: string;
  athleteId: string;
  athleteName: string;
  proposed: ChangeRequestFields;
  /** What the record holds now; only reviewers (athlete managers) receive it. */
  current?: ChangeRequestFields;
  /** A name or birth date changes: the review should check a document. */
  sensitive: boolean;
  note: string | null;
  status: ChangeRequestStatus;
  decisionReason: string | null;
  createdAt: string;
  decidedAt: string | null;
  /** Row version; send it in `If-Match` when deciding or withdrawing. */
  version: number;
}
